import { Api } from '../constants/Api';
import { SocketMessage } from '../types';
import { isProduction } from '../../constants/environment';

export type MessageHandler = (message: SocketMessage) => void;

export class SocketService {
  private socket: WebSocket | null = null;
  private messageHandlers: Map<string, MessageHandler[]> = new Map();
  private token: string = '';
  private socketType: string | undefined = undefined;
  public reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private onReconnectCallbacks: (() => void)[] = [];
  private skipReconnectFromClose: boolean = false;

  connect(token: string, socketType?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.token = token;
      this.socketType = socketType;

      try {
        // Connect directly to backend - backend now accepts token from query parameter
        // Use the current hostname for development (works for localhost and network IP)
        // In production: use production server URL
        // socketType can be: "game", "lobby", "dashboard" - used to filter messages on backend
        let wsUrl: string;
        const socketTypeParam = socketType ? `&socketType=${encodeURIComponent(socketType)}` : '';
        if (isProduction) {
          wsUrl = `${Api.WEBSOCKET_URL}?token=${encodeURIComponent(token)}${socketTypeParam}`;
        } else {
          // In development, use the current hostname so it works from network devices
          // This way, when accessing from phone at 192.168.2.16, it connects to ws://192.168.2.16:8080
          const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
          wsUrl = `ws://${hostname}:8080/ws?token=${encodeURIComponent(token)}${socketTypeParam}`;
        }
        
        console.log('Connecting to WebSocket:', wsUrl, socketType ? `(type: ${socketType})` : '');
        
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('Connected to server');
          this.socket = ws;
          const wasReconnecting = this.reconnectAttempts > 0;
          this.reconnectAttempts = 0;
          
          // Don't send AUTH message - the server checks Authorization header
          // The proxy (in dev) or server (in prod) should handle token from query param
          
          resolve();
          
          // If this was a reconnection, call reconnect callbacks AFTER resolve
          // This ensures the connection is fully established
          if (wasReconnecting) {
            console.log('WebSocket reconnected, calling reconnect callbacks');
            // Use setTimeout to ensure this happens after the promise resolves
            setTimeout(() => {
              this.onReconnectCallbacks.forEach(callback => {
                try {
                  callback();
                } catch (error) {
                  console.error('Error in reconnect callback:', error);
                }
              });
            }, 100);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };

        ws.onclose = (event) => {
          console.log('Disconnected from server', event.code, event.reason);
          this.socket = null;
          
          // Error code 1011 means Internal Server Error - usually authentication failure
          // Code 1008 means policy violation - often used for authentication failures
          if (event.code === 1011 || event.code === 1008) {
            console.error('WebSocket authentication failed. Server rejected connection.');
            // Clear invalid token and redirect to login
            if (typeof window !== 'undefined') {
              localStorage.removeItem('token');
              localStorage.removeItem('userId');
              localStorage.removeItem('username');
              localStorage.removeItem('userEmail');
              localStorage.removeItem('emailVerified');
              window.location.href = '/login';
            }
            // Only reject if promise hasn't been resolved yet
            if (!this.socket) {
              reject(new Error('Authentication failed. Please login again.'));
            }
            return;
          }

          if (this.skipReconnectFromClose) {
            return;
          }
          
          // Code 1005 means "No Status Received" - server closed without sending close frame
          // This can happen if server closes connection abruptly (e.g., after CREATE_ROOM)
          // Don't reject the promise - let reconnection handle it
          const wasReconnecting = this.reconnectAttempts > 0;
          
          // Attempt to reconnect only if not a normal closure and not auth error
          if (event.code !== 1000 && event.code !== 1011 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = this.reconnectDelay * Math.min(this.reconnectAttempts, 3); // Exponential backoff, max 3x
            setTimeout(() => {
              console.log(`Reconnecting... (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
              this.connect(this.token, this.socketType).catch((error) => {
                console.error('Reconnection failed:', error);
                // If this was the last attempt, we might want to notify
                if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                  console.error('Max reconnection attempts reached');
                }
              });
            }, delay);
          }
          // Note: We no longer resolve on code 1000 in onclose - connection should resolve on onopen
        };

        ws.onmessage = async (event) => {
          try {
            let messageData: string;
            
            // Handle both text and Blob messages
            if (event.data instanceof Blob) {
              messageData = await event.data.text();
            } else if (typeof event.data === 'string') {
              messageData = event.data;
            } else {
              // Try to convert ArrayBuffer to string
              messageData = new TextDecoder().decode(event.data);
            }
            
            console.log('Raw WebSocket message received:', messageData);
            const data = JSON.parse(messageData);
            const type = data.type;
            console.log('Parsed message type:', type, 'Full data:', data);
            
            if (type) {
              this.handleMessage(type, data);
            } else {
              // If no type, try to handle as generic message
              console.warn('Message without type:', data);
            }
          } catch (error) {
            console.error('Error parsing message:', error, event.data);
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      console.log('SocketService.disconnect() called - closing WebSocket');
      console.trace('Disconnect call stack');
      this.socket.close();
      this.socket = null;
    } else {
      console.log('SocketService.disconnect() called but socket is already null');
    }
    // Don't clear handlers here - let the component decide when to clear them
    // Clearing here causes issues with React StrictMode remounts
  }

  /**
   * Close the current connection and reconnect. Use when the page becomes visible again
   * after being in the background so messages flow again (desktop/minimized window or mobile).
   */
  forceReconnect(): Promise<void> {
    this.skipReconnectFromClose = true;
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.reconnectAttempts = 1;
    return this.connect(this.token, this.socketType).finally(() => {
      this.skipReconnectFromClose = false;
    });
  }

  sendMessage(message: SocketMessage): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('Sending message:', message);
      this.socket.send(JSON.stringify(message));
    } else {
      console.error('Socket not connected');
    }
  }

  onMessage(messageType: string, handler: MessageHandler): void {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, []);
    }
    this.messageHandlers.get(messageType)!.push(handler);
  }

  clearAllHandlers(): void {
    this.messageHandlers.clear();
    this.onReconnectCallbacks = [];
  }

  offMessage(messageType: string, handler: MessageHandler): void {
    const handlers = this.messageHandlers.get(messageType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private handleMessage(type: string, data: any): void {
    const message: SocketMessage = { type, ...data };
    const handlers = this.messageHandlers.get(type);
    console.log(`Handling message type: ${type}, handlers registered: ${handlers ? handlers.length : 0}`);
    if (handlers) {
      handlers.forEach((handler) => {
        console.log(`Calling handler for type: ${type}`);
        handler(message);
      });
    } else {
      console.warn(`No handlers registered for message type: ${type}`);
      console.log('Available handlers:', Array.from(this.messageHandlers.keys()));
    }
  }

  isConnected(): boolean {
    console.log('SocketService.isConnected() called, socket:', this.socket, 'readyState:', this.socket?.readyState);
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  onReconnect(callback: () => void): void {
    this.onReconnectCallbacks.push(callback);
  }

  offReconnect(callback: () => void): void {
    const index = this.onReconnectCallbacks.indexOf(callback);
    if (index > -1) {
      this.onReconnectCallbacks.splice(index, 1);
    }
  }
}

